import { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/supabase';
import { useAtom, useSetAtom } from 'jotai';
import { logger } from '../lib/logger';
import {
  userAtom,
  sessionAtom,
  profileAtom,
  authLoadingAtom,
  isRecoverySessionAtom,
  subscriptionUsageAtom,
  subscriptionLoadingAtom,
} from '../state/authAtoms';
import { getCurrentSubscriptionUsage } from '../lib/subscriptions';
import { identifySentryUser, clearSentryUser } from '../lib/sentry/user';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  isRecoverySession: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null; data: any }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; emailNotConfirmed?: boolean }>;
  signInWithGoogle: (redirectUrl?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resendConfirmationEmail: (email: string) => Promise<{ error: Error | null }>;
  sendPasswordResetEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useAtom(userAtom);
  const [profile, setProfile] = useAtom(profileAtom);
  const [session, setSession] = useAtom(sessionAtom);
  const [loading, setLoading] = useAtom(authLoadingAtom);
  const [isRecoverySession, setIsRecoverySession] = useAtom(isRecoverySessionAtom);
  const setSubscriptionUsage = useSetAtom(subscriptionUsageAtom);
  const setSubscriptionLoading = useSetAtom(subscriptionLoadingAtom);
  
  // Track if we've detected a recovery session - once detected, keep it until password is updated
  const recoverySessionDetectedRef = useRef<boolean>(false);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (!error && data) {
      setProfile(data);
    }
  };

  const fetchSubscriptionUsage = async (userId: string) => {
    setSubscriptionLoading(true);
    try {
      const usage = await getCurrentSubscriptionUsage(userId);
      setSubscriptionUsage(usage);
    } catch (error) {
      logger.error('Error fetching subscription usage:', error);
      setSubscriptionUsage(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await Promise.all([
        fetchProfile(user.id),
        fetchSubscriptionUsage(user.id),
      ]);
    }
  };

  useEffect(() => {
    // Helper to check if current context indicates a recovery session
    const checkIfRecoverySession = (): boolean => {
      // If we're on the reset-password route, any session MUST be recovery
      if (window.location.pathname === '/reset-password') {
        return true;
      }
      
      // Check URL hash for recovery type
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      if (type === 'recovery') {
        return true;
      }
      
      // Check query params as well (Supabase might use either)
      const queryParams = new URLSearchParams(window.location.search);
      const queryType = queryParams.get('type');
      if (queryType === 'recovery') {
        return true;
      }
      
      return false;
    };

    // Check initial session - be very conservative
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      
      if (session?.user) {
        // If we're on reset-password route or detect recovery params, treat as recovery
        if (checkIfRecoverySession()) {
          recoverySessionDetectedRef.current = true;
          setIsRecoverySession(true);
          setUser(null); // Don't set user for recovery sessions
          setProfile(null);
          setSubscriptionUsage(null);
        } else {
          // Only set user if we're NOT on reset-password route and no recovery indicators
          setIsRecoverySession(false);
          setUser(session.user);
          fetchProfile(session.user.id);
          fetchSubscriptionUsage(session.user.id);
          // Identify user in Sentry for error correlation
          identifySentryUser(session.user.id);
        }
      } else {
        setIsRecoverySession(false);
        setUser(null);
        setSubscriptionUsage(null);
        // Clear Sentry user when not logged in
        clearSentryUser();
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      (async () => {
        setSession(session);

        // Handle recovery sessions - don't treat them as logged in
        if (event === 'PASSWORD_RECOVERY') {
          recoverySessionDetectedRef.current = true;
          setIsRecoverySession(true);
          setUser(null); // Don't set user for recovery sessions
          setProfile(null);
          setSubscriptionUsage(null);
        } else if (event === 'INITIAL_SESSION') {
          // Check if this initial session is a recovery session
          if (checkIfRecoverySession()) {
            recoverySessionDetectedRef.current = true;
            setIsRecoverySession(true);
            setUser(null); // Don't set user for recovery sessions
            setProfile(null);
            setSubscriptionUsage(null);
          } else if (recoverySessionDetectedRef.current) {
            // We previously detected a recovery session - keep it as recovery
            setIsRecoverySession(true);
            setUser(null);
            setProfile(null);
            setSubscriptionUsage(null);
          } else {
            // Normal initial session - user is logged in
            setIsRecoverySession(false);
            setUser(session?.user ?? null);
            if (session?.user) {
              await fetchProfile(session.user.id);
              await fetchSubscriptionUsage(session.user.id);
              // Identify user in Sentry
              identifySentryUser(session.user.id);

              // Note: Pending subscription check moved to usePendingSubscription hook
              // This ensures redirect happens in the tab where user is looking (email confirmation tab)
              // Not in the original signup tab which might be in background
            } else {
              setProfile(null);
              setSubscriptionUsage(null);
              clearSentryUser();
            }
          }
        } else if (event === 'SIGNED_IN') {
          // SIGNED_IN event - check if we have a recovery session detected
          if (recoverySessionDetectedRef.current || window.location.pathname === '/reset-password') {
            // Recovery session detected or still on reset-password - keep as recovery
            setIsRecoverySession(true);
            setUser(null);
            setProfile(null);
            setSubscriptionUsage(null);
          } else {
            // Not a recovery session - this is a real login
            setIsRecoverySession(false);
            setUser(session?.user ?? null);
            if (session?.user) {
              await fetchProfile(session.user.id);
              await fetchSubscriptionUsage(session.user.id);
              // Identify user in Sentry
              identifySentryUser(session.user.id);

              // Note: Pending subscription check moved to usePendingSubscription hook
              // This ensures redirect happens in the tab where user is looking
            } else {
              setProfile(null);
              setSubscriptionUsage(null);
              clearSentryUser();
            }
          }
        } else if (
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        ) {
          // USER_UPDATED fires after password is successfully changed
          // Clear recovery flag and set user
          recoverySessionDetectedRef.current = false;
          setIsRecoverySession(false);
          setUser(session?.user ?? null);
          if (session?.user) {
            await fetchProfile(session.user.id);
            await fetchSubscriptionUsage(session.user.id);
            // Identify user in Sentry
            identifySentryUser(session.user.id);
          } else {
            setProfile(null);
            setSubscriptionUsage(null);
            clearSentryUser();
          }
        } else if (event === 'SIGNED_OUT') {
          recoverySessionDetectedRef.current = false;
          setIsRecoverySession(false);
          setUser(null);
          setProfile(null);
          setSubscriptionUsage(null);
          // Clear Sentry user on sign out
          clearSentryUser();
        }
        
        setLoading(false);
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/auth?source=email-confirmation`,
        },
      });

      if (error) throw error;

      // Return the user data so SignupForm can use the user_id
      return { error: null, data };
    } catch (error) {
      return { error: error as Error, data: null };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Check if the error is due to unconfirmed email
        const isEmailNotConfirmed = error.message.toLowerCase().includes('email not confirmed');
        return { error: error as Error, emailNotConfirmed: isEmailNotConfirmed };
      }
      return { error: null };
    } catch (error) {
      const err = error as Error;
      const isEmailNotConfirmed = err.message?.toLowerCase().includes('email not confirmed');
      return { error: err, emailNotConfirmed: isEmailNotConfirmed };
    }
  };

  const resendConfirmationEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?source=email-confirmation`,
        },
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signInWithGoogle = async (redirectUrl?: string) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
            ? `${window.location.origin}${redirectUrl}`
            : `${window.location.origin}/dashboard`,
        },
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    user,
    profile,
    session,
    loading,
    isRecoverySession,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    refreshProfile,
    resendConfirmationEmail,
    sendPasswordResetEmail,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
